import { Popover, Transition } from "@headlessui/react";
import {
	ArrowDownRightIcon,
	ArrowUpRightIcon,
	CheckCircleIcon,
	InformationCircleIcon,
	XCircleIcon
} from "@heroicons/react/24/outline"
import { DonutChart } from "@mantine/charts"
import { Image, Popover as PopoverMantine } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks";
import React from "react"
import { useContentContext } from "../utils/ContentProvider"
import { getInsuranceCompanyName } from "../utils/helpers";
import { getVisibleValue } from "../utils/options"
import { sendEvent } from "../utils/queries";
import { getRuntimeReferrer, isVertaaensinHost } from "../utils/runtimeOverrides"
import { ResultRowInterface } from "../utils/types"

const SingleResultRow = (props: ResultRowInterface) => {
	const { lang, translations } = useContentContext()
	const [opened, { close, open }] = useDisclosure(false)
	const referrerLower = getRuntimeReferrer().toLowerCase()
	const referrerIsEffortia: boolean = referrerLower.includes("vakuutustenvertailu") ||
		referrerLower.includes("vakuutusvertailu") ||
		referrerLower.includes("effortia")
	const shouldHideLogo = props.insuranceCompanyName?.trim().toLowerCase() === "(nimi piilotettu)"
	const isVertaaensinTheme = isVertaaensinHost() || referrerLower.includes("vertaaensin")
	const primaryCtaClass = isVertaaensinTheme
		? "bg-[#f58423] hover:bg-[#f58423] text-white"
		: "bg-[#0F6CBD] hover:bg-[#0c589a] text-white"

	let epsiDifference: number = 0
	if ( props.rating.insurerepsi > 0 ) {
		epsiDifference = props.rating.insurerepsi - props.rating.epsi
	}

	return (
		<div className="px-6 py-4 rounded-md bg-white" onClick={ () => props.onShowInsuranceCompany() }>
			<div
				className="flex flex-col lg:flex-row items-start lg:items-center lg:justify-between space-y-4 lg:space-y-0">
				<div className="flex flex-col items-evenly w-[150px] space-y-2 break-words">
					<div>{ props.place }.</div>
					<div className="font-bold">{ props.insuranceCompanyName }</div>
					<div>{ getVisibleValue(props.group, lang) }</div>
				</div>
				<div className="flex flex-col items-center w-[100px]">
					{ shouldHideLogo ? (
						<div className="w-[100px] h-[100px]" aria-hidden="true"/>
					) : (
						<Image
							radius="md"
							w={ 100 }
							h={ 100 }
							fit="contain"
							src={ getInsuranceCompanyName(props.insuranceCompanyName) }
						/>
					) }
				</div>
				<div className="flex flex-col items-evenly md:w-[235px] px-2">
					{ (!props.rating.positive || props.rating.positive.length === 0) && (
						<div className="flex flex-row items-center min-w-[165px]"></div>
					) }
					{ props.rating.positive && props.rating.positive.length > 0 && props.rating.positive.map((item, index) => {
						return (
							<div key={ index } className="flex flex-row items-top">
								{ item.icon === 1 &&
                  <CheckCircleIcon className="flex shrink-0 w-4 h-4 text-green-600 mt-1 mr-2"/> }
								{ item.icon === 0 &&
                  <InformationCircleIcon
                    className="flex shrink-0 w-4 h-4 text-gray-600 mt-1 mr-2"/> }
								{ item.icon === -1 &&
                  <XCircleIcon className="flex shrink-0 w-4 h-4 text-red-600 mt-1 mr-2"/> }
								<div>{ item.note }</div>
							</div>
						)
					}) }
					{ props.rating.negative && props.rating.negative.length > 0 && props.rating.negative.map((item, index) => {
						return (
							<div key={ index } className="flex flex-row items-top">
								{ item.icon === 1 &&
                  <CheckCircleIcon className="flex shrink-0 w-4 h-4 text-green-600 mt-1 mr-2"/> }
								{ item.icon === 0 &&
                  <InformationCircleIcon
                    className="flex shrink-0 w-4 h-4 text-gray-600 mt-1 mr-2"/> }
								{ item.icon === -1 &&
                  <XCircleIcon className="flex shrink-0 w-4 h-4 text-red-600 mt-1 mr-2"/> }
								{ item.note }
							</div>
						)
					}) }
				</div>
				<div className="relative flex flex-row items-center justify-center w-[70px] h-[70px] mx-2">
					{/*{ [ ...Array(Math.floor(props.rating.epsi)) ].map((option, index) => (*/ }
					{/*	<StarIcon className="w-4 h-4 text-black" key={ "StarFilled" + index }/>*/ }
					{/*)) }*/ }
					{/*{ [ ...Array(5 - Math.floor(props.rating.epsi)) ].map((option, index) => (*/ }
					{/*	<StarIconOutline className="w-4 h-4 text-black" key={ "StarOutlined" + index }/>*/ }
					{/*)) }*/ }
					{ props.rating.epsi && (
						<PopoverMantine width={540} position="bottom" withArrow shadow="md" opened={opened}>
							<PopoverMantine.Target>
								<div className="flex flex-row items-center justify-center" onMouseEnter={open} onMouseLeave={close}>
									<div className="absolute left-0 bottom-0 w-[70px] h-[70px]">
										<DonutChart
											data={ [
												{ name: 'EPSI', value: props.rating.insurerepsi, color: "#0F6CBD" },
												{ name: "", value: 100 - props.rating.insurerepsi, color: "gray.3" },
												// { name: "", value: 45, color: "gray.3" },
											] }
											withLabelsLine={ false }
											withLabels={ false }
											withTooltip={ false }
											chartLabel={ "" }
											size={ 70 }
											thickness={ 5 }
											paddingAngle={ 1 }
											startAngle={ 225 }
											endAngle={ -45 }
											className="font-semibold"
										/>
									</div>
									<div className="absolute left-0 bottom-0 w-[70px] h-[70px]">
										<div
											className="flex flex-col items-center justify-center w-[70px] h-[70px]">
											<div className="text-sm font-semibold text-[#0F6CBD]">
												<span>{ props.rating.insurerepsi > 0 ? props.rating.insurerepsi : "N/A" }</span>
											</div>
											<Popover className="relative">
												{ props.rating.insurerepsi <= 0 &&
                          <div className="absolute -top-8 -right-6">
                            <Popover.Button>
                              <InformationCircleIcon className="w-4 h-4 bg-white rounded-full"/>
                            </Popover.Button>
                            <Transition
                              enter="transition duration-100 ease-out"
                              enterFrom="transform opacity-0"
                              enterTo="transform opacity-100"
                              leave="transition duration-75 ease-out"
                              leaveFrom="transform opacity-100"
                              leaveTo="transform opacity-0">
                              <Popover.Panel className="absolute z-10 left-8 -top-10">
                                <div
                                  className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
																	{ translations.singleResult.noEPSIrating }
                                </div>
                              </Popover.Panel>
                            </Transition>
                          </div>
												}
											</Popover>
											{ epsiDifference > 0 &&
                        <div className="flex flex-row items-center text-xs font-medium text-green-800">
                          <ArrowUpRightIcon
                            className="w-3.5 h-3.5 pt-0.5 pr-1"/> { epsiDifference.toFixed(1) }
                        </div>
											}
											{ epsiDifference < 0 &&
                        <div className="flex flex-row items-center text-xs font-medium text-red-800">
                          <ArrowDownRightIcon
                            className="w-3.5 h-3.5 pt-0.5 pr-1"/> { epsiDifference.toFixed(1) }
                        </div>
											}
										</div>
									</div>
								</div>
							</PopoverMantine.Target>
							<PopoverMantine.Dropdown style={{ pointerEvents: 'none' }}>
								<div>Asiakastyytyväisyys perustuu puolueettomaan EPSi-tutkimukseen, jossa selvitetään asiakkaiden tyytyväisyyttä, uskollisuutta, yhtiön imagoa, tuotteiden ja palveluiden laatua sekä kokemusta vastineesta rahalle. EPSI:n asiakastyytyväisyys mitataan asteikolla 0-100.
								</div>
							</PopoverMantine.Dropdown>
						</PopoverMantine>
					) }
				</div>
				<div className="flex flex-row items-center justify-center min-w-[125px]">
					<div className="font-bold text-xl">{ props.price } €</div>
				</div>
				<div className="flex flex-row items-center justify-center">
					<div
						onClick={ () => {
							sendEvent(props.token, referrerIsEffortia, {
								category: 'showMore',
								action: 'click',
								label: props.insuranceCompanyName,
								gclid: props.gclid ?? ""
							})

							props.onShowInsuranceCompany()
						} }
						className={`${primaryCtaClass} my-2 duration-200 border py-3 px-4 font-semibold text-md rounded-md transition-all`}>
						{ translations.singleResult.showMore }
					</div>
				</div>
			</div>
			{/*<div className="flex flex-col items-center justify-center mt-4">*/ }
			{/*	<Disclosure>*/ }
			{/*		{({open}) => (*/ }
			{/*			<>*/ }
			{/*				<Disclosure.Button className="flex flex-row justify-center items-center">*/ }
			{/*					<ChevronRightIcon*/ }
			{/*						className={`${*/ }
			{/*							open ? 'rotate-90 transform' : ''*/ }
			{/*						} h-4 w-4 mr-2`}*/ }
			{/*					/>*/ }
			{/*					Show details*/ }
			{/*				</Disclosure.Button>*/ }
			{/*				<Disclosure.Panel className="w-full py-4">*/ }
			{/*					{props.coverages.map((item, index) => {*/ }
			{/*						return (*/ }
			{/*							<div key={"Coverage" + index}*/ }
			{/*							     className="flex flex-col lg:flex-row items-start lg:items-center justify-start border-b border-gray-300 mb-4 pb-4">*/ }
			{/*								<div className="min-w-[200px]">*/ }
			{/*									<div>{item.name}</div>*/ }
			{/*								</div>*/ }
			{/*								{item.rating &&*/ }
			{/*									<div className="flex flex-row items-center justify-center">*/ }
			{/*										{[...Array(item.rating)].map((option, index) => (*/ }
			{/*											<StarIcon className="w-4 h-4 text-black"/>*/ }
			{/*										))}*/ }
			{/*										{[...Array(5 - item.rating)].map((option, index) => (*/ }
			{/*											<StarIconOutline className="w-4 h-4 text-black"/>*/ }
			{/*										))}*/ }
			{/*									</div>*/ }
			{/*								}*/ }
			{/*								{item.text &&*/ }
			{/*									<div>*/ }
			{/*										<div className="text-left">{item.text}</div>*/ }
			{/*									</div>*/ }
			{/*								}*/ }
			{/*							</div>*/ }
			{/*						)*/ }
			{/*					})}*/ }
			{/*				</Disclosure.Panel>*/ }
			{/*			</>*/ }
			{/*		)}*/ }
			{/*	</Disclosure>*/ }
			{/*</div>*/ }
		</div>
	)
}

export default SingleResultRow
